import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, signOut } from 'firebase/auth';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import '../pagesCSS/signOnUp.css';
import { GoogleUserAuth } from '../FireBaseConfig/AuthContext';

const UserAuth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isUnverified, setIsUnverified] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { googleSignIn } = GoogleUserAuth();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        if (user.emailVerified) {
          navigate('/list');
        } else {
          await signOut(auth);
          setIsUnverified(true);
          setError('Please verify your email before signing in.');
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, auth]);

  const startTimer = () => {
    setResendDisabled(true);
    setTimer(60);
    const interval = setInterval(() => {
      setTimer(prevTime => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  const handleResendVerification = async () => {
    if (!resendDisabled) {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
        await signOut(auth);
        startTimer();
        setError('');
        setSuccessMessage('A new verification email has been sent. Please check your inbox.');
      } catch (error) {
        console.error("Error resending verification:", error);
        setError('Failed to resend verification email. Please check your credentials.');
      }
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailSignIn = async () => {
    setError('');
    setSuccessMessage('');
    
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    
    if (!isValidEmail(email)) {
      setError('Invalid email address');
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password.');
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        await signOut(auth);
        setIsUnverified(true);
        setError('Please verify your email before signing in.');
      }
    } catch (error) {
      console.error("Error during email sign-in: ", error);
      
      switch(error.code) {
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email. Please check your email or sign up.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later or reset your password.');
          break;
        case 'auth/invalid-login-credentials':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-credential':
          setError('Incorrect password. Please try again.');
          break;
        default:
          setError('Incorrect password. Please try again.');
      }
    }
  };

  const handleGoogleSignInClick = async () => {
    setError('');
    setSuccessMessage('');
    
    try {
      const result = await googleSignIn();
      if (!result?.user) {
        setError('No user data received. Please try again.');
        return;
      }
      navigate("/list");
    } catch (error) {
      console.error("Google sign in error:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign in was cancelled. Please try again.');
      } else {
        setError('An error occurred during sign-in. Please try again.');
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!isValidEmail(email)) {
      setError('Please enter a valid email to send reset password link.');
      return;
    }
  
    try {
      await sendPasswordResetEmail(auth, email);
      setError('');
      setSuccessMessage('A password reset link has been sent to your email.');
    } catch (error) {
      console.error("Error sending password reset email: ", error);
      setError('Failed to send password reset email.');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="auth-page">
      <motion.div 
        className="auth-card"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header with icon */}
        <motion.div className="auth-header" variants={itemVariants}>
          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-description">Sign in to WiCSEpedia to continue</p>
        </motion.div>

        {/* Google Sign In */}
        <motion.div variants={itemVariants}>
          <button className="google-sign-in-btn" onClick={handleGoogleSignInClick}>
            <span className="google-icon-modern"></span>
            <span>Continue with Google</span>
          </button>
        </motion.div>

        {/* Divider */}
        <motion.div className="auth-divider" variants={itemVariants}>
          <span className="divider-line"></span>
          <span className="divider-text">or</span>
          <span className="divider-line"></span>
        </motion.div>

        {/* Error/Success Messages */}
        {error && (
          <motion.div 
            className="message-box error-box"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <AlertCircle className="message-icon" />
            <span>{error}</span>
          </motion.div>
        )}

        {successMessage && (
          <motion.div 
            className="message-box success-box"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <CheckCircle className="message-icon" />
            <span>{successMessage}</span>
          </motion.div>
        )}

        {/* Email Input */}
        <motion.div className="input-group" variants={itemVariants}>
          <label className="input-label" htmlFor='email'>Email address</label>
          <div className="input-wrapper">
            <Mail className="input-icon" />
            <input 
              id = "email"
              type="email" 
              className="modern-input" 
              placeholder="you@example.com" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </motion.div>

        {/* Password Input */}
        <motion.div className="input-group" variants={itemVariants}>
          <label className="input-label" htmlFor='password'>Password</label>
          <div className="input-wrapper">
            <Lock className="input-icon" />
            <input 
              id = "password"
              type={showPassword ? "text" : "password"}
              className="modern-input password-input-field" 
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button 
              id = "password-toggle"
              type="button"
              className="password-toggle"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </motion.div>

        {/* Forgot Password */}
        <motion.div variants={itemVariants}>
          <button className="forgot-password-btn" onClick={handleForgotPassword}>
            Forgot your password?
          </button>
        </motion.div>

        {/* Sign In Button */}
        <motion.div variants={itemVariants}>
          {isUnverified ? (
            <button 
              className="primary-btn"
              onClick={handleResendVerification}
              disabled={resendDisabled}
            >
              {resendDisabled ? `Resend Verification (${timer}s)` : 'Resend Verification Email'}
            </button>
          ) : (
            <button className="primary-btn" onClick={handleEmailSignIn}>
              Sign In
            </button>
          )}
        </motion.div>

        {/* Sign Up Link */}
        <motion.div className="auth-footer" variants={itemVariants}>
          <p className="footer-text">
            Don't have an account? <Link to="/signup" className="footer-link">Sign up</Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default UserAuth;